import React, { useState } from "react";
import {
  AppBar, Toolbar, Typography, Box, Chip,
  IconButton, Avatar, Menu, MenuItem, ListItemIcon, Divider, Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DrawerMenu from "./DrawerMenu";
import NotificationBell from "./NotificationBell";
import AssistantDrawer from "./Assistant/AssistantDrawer";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../reduxStore/Store";
import { setSelectedDepartement } from "../reduxStore/AppSlice";
import { forceLogout } from "../utils/authActions";
import { useNotificationListener } from "../hooks/useNotificationListener";
import BusinessIcon from "@mui/icons-material/Business";
import PublicIcon from "@mui/icons-material/Public";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";

const Navbar: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { selectedDepartement, isAllMode } = useSelector((state: RootState) => state.AppSlice);
  const { currentUser } = useSelector((state: RootState) => state.AuthSlice);
  const allowedDepartements = useSelector((state: RootState) => state.ScopeSlice.allowedDepartements);
  const isMonoDepartement = allowedDepartements.length === 1;

  // Branche le hook qui écoute WS NOTIFICATION_NEW → Redux + toast si popup
  useNotificationListener();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleChangeDepartement = () => {
    dispatch(setSelectedDepartement(null));
  };

  const handleLogout = () => {
    setAnchorEl(null);
    forceLogout('manual');   // centralisé : reset Redux, vide localStorage,
                              // coupe WS, redirige hard /login
  };

  // Initiales pour l'avatar
  const initials = currentUser?.login
    ? currentUser.login.slice(0, 2).toUpperCase()
    : '?';

  return (
    <AppBar position="fixed">
      <Toolbar sx={{ gap: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 0 }}>
          <DrawerMenu />
        </Typography>

        <Tooltip title="Accueil">
          <IconButton color="inherit" onClick={() => navigate('/')} size="small">
            <HomeIcon />
          </IconButton>
        </Tooltip>

        <Box sx={{ flexGrow: 1 }} />

        {/* Rien à changer avec un seul département autorisé — le Chip n'a pas lieu d'être. */}
        {(selectedDepartement || isAllMode) && !isMonoDepartement && (
          <Chip
            icon={isAllMode ? <PublicIcon /> : <BusinessIcon />}
            label={isAllMode ? 'Tous les départements' : selectedDepartement!.nom}
            onClick={handleChangeDepartement}
            title="Cliquer pour changer de département"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer',
                  '& .MuiChip-icon': { color: 'white' } }}
          />
        )}

        {/* Assistant IA — masqué sans la permission assistant.use */}
        <AssistantDrawer />

        <NotificationBell />

        {/* Avatar utilisateur + menu */}
        <Tooltip title={currentUser?.login ?? 'Compte'}>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" sx={{ ml: 1 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'rgba(255,255,255,0.25)' }}>
              {initials}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{ paper: { elevation: 3, sx: { minWidth: 180, mt: 1 } } }}
        >
          <MenuItem disabled sx={{ opacity: '1 !important' }}>
            <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
            <Box>
              <Typography variant="body2" fontWeight={600}>{currentUser?.login}</Typography>
              <Typography variant="caption" color="text.secondary">{currentUser?.email}</Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
            <Typography variant="body2" color="error">Se déconnecter</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
